// ADR 7.10.26v §1 — what the browser says about folds and pointers, in dp.
//
// Feature-detected, never sniffed. `window.viewport.segments` is Chromium's
// Viewport Segments API (138+), absent from TypeScript's DOM lib, so it is read
// through a local interface behind `typeof` checks; WebKit and Firefox report
// nothing and the rule decides by size alone (roadmap 7.10.26t §4). A reported
// pair means the fold is *separating*, so two segments are always half-open.
// Three or more are no shipped device and read as no fold.
//
// The media queries are change *notifications* only: the geometry comes from
// the segment rects, divided by the same root scale `useWindowClass` divides
// the width by, so the fold and the width share one unit.

import type { FoldOrientation, FoldState } from '../../wasm'

/** What the browser says about folds and pointers, in dp. Never throws. */
export interface FoldSignals {
  fold: FoldOrientation
  foldState: FoldState
  /** Physical: the fold's left (or top) edge, never mirrored for RTL. */
  foldOffsetDp: number
  /** The occluded extent; 0 for a seamless fold. */
  foldSpanDp: number
  touchPrimary: boolean
}

// One identity table per enum: the mapped type makes a misspelt word a compile
// error, and every comparison below goes through a key rather than a literal.
const AXIS = { none: 'none', vertical: 'vertical', horizontal: 'horizontal' } as const satisfies {
  [K in FoldOrientation]: K
}
const OPENING = { flat: 'flat', half_open: 'half_open' } as const satisfies { [K in FoldState]: K }

const NO_FOLD = { fold: AXIS.none, foldState: OPENING.flat, foldOffsetDp: 0, foldSpanDp: 0 }

/** No fold and a fine pointer: what a reading that does not ask the spread passes. */
export const NO_FOLD_SIGNALS: FoldSignals = { ...NO_FOLD, touchPrimary: false }

/** The three queries whose `change` means "read the signals again". */
const WATCHED_QUERIES = [
  '(horizontal-viewport-segments: 2)',
  '(vertical-viewport-segments: 2)',
  '(pointer: coarse)',
]

interface SegmentRect {
  left: number
  right: number
  top: number
  bottom: number
}

/** The slice of Chromium's `window.viewport` this module reads. */
interface ViewportWithSegments {
  segments?: ArrayLike<SegmentRect> | null
}

function segments(): SegmentRect[] {
  const viewport = (window as unknown as { viewport?: ViewportWithSegments }).viewport
  const list = typeof viewport === 'object' && viewport !== null ? viewport.segments : null
  if (list === null || list === undefined || typeof list.length !== 'number') return []
  return Array.from(list)
}

function query(media: string): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(media) : null
}

function foldOf(scale: number): Omit<FoldSignals, 'touchPrimary'> {
  const found = segments()
  if (found.length !== 2) return NO_FOLD
  const [first, second] = found as [SegmentRect, SegmentRect]
  const dp = (px: number) => Math.round(px / scale)
  if (second.left >= first.right) {
    return { fold: AXIS.vertical, foldState: OPENING.half_open, foldOffsetDp: dp(first.right), foldSpanDp: dp(second.left - first.right) }
  }
  if (second.top >= first.bottom) {
    return { fold: AXIS.horizontal, foldState: OPENING.half_open, foldOffsetDp: dp(first.bottom), foldSpanDp: dp(second.top - first.bottom) }
  }
  return NO_FOLD
}

/** The fold and the pointer, now. `scale` is the root scale (CSS px per dp). */
export function readFoldSignals(scale: number): FoldSignals {
  try {
    return { ...foldOf(scale), touchPrimary: query('(pointer: coarse)')?.matches ?? false }
  } catch {
    return NO_FOLD_SIGNALS
  }
}

/** The direction `i18n/rtl.ts` set on the root (the read `useSeamDrag.ts` makes). */
export function readRtl(): boolean {
  return document.documentElement.dir === 'rtl'
}

/** Calls `onChange` when a segment query, the pointer or the root's `dir` moves. */
export function watchFoldSignals(onChange: () => void): () => void {
  const lists = WATCHED_QUERIES.map(query).filter((list): list is MediaQueryList => list !== null)
  lists.forEach((list) => list.addEventListener?.('change', onChange))
  const observer = typeof MutationObserver === 'function' ? new MutationObserver(onChange) : null
  observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] })
  return () => {
    lists.forEach((list) => list.removeEventListener?.('change', onChange))
    observer?.disconnect()
  }
}
